/**
 * Programs view component.
 * @module components/theme/View/ProgramsView
 */

import React from 'react';
import { useSelector } from 'react-redux';

import RadioLiveView from '../../../Blocks/RadioLive/RadioLiveView';
import RadioScheduleView from '../../../Blocks/RadioSchedule/RadioScheduleView';
import PodcastsView from '../../../Blocks/PodcastEpisodes/PodcastEpisodesView';

import {
  getPrograms,
  normalizePrograms,
} from '../../../../services/api/programs';
import { selectViewData, VIEW_DATA_KEYS } from '../viewSsrData';
import type { ProgramItem } from '../../../shared/ProgramCarouselSection/ProgramCarouselSection.types';

import ProgramsGrid from './components/ProgramsGrid';
import ProgramsSearch from './components/ProgramsSearch';

import { AdManagerSlot } from '../../../../components/AdManagerSlot';

import './ProgramsView.less';

const PROGRAMS_PAGE_SIZE = 12;

export default function ProgramsView() {
  const preloadedData = useSelector((state: any) =>
    selectViewData(state, VIEW_DATA_KEYS.programs),
  );
  const hasPreloadedData = Array.isArray(preloadedData);
  const initialPrograms = normalizePrograms(preloadedData);
  const [programs, setPrograms] =
    React.useState<ProgramItem[]>(initialPrograms);
  const [loading, setLoading] = React.useState(!hasPreloadedData);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [showArchive, setShowArchive] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(
    hasPreloadedData ? initialPrograms.length === PROGRAMS_PAGE_SIZE : true,
  );

  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const gridWrapperRef = React.useRef<HTMLDivElement | null>(null);

  const searchTerm = query.trim();
  const isSearching = !!searchTerm;
  const hasPrograms = programs.length > 0;

  const isMobile = React.useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 767;
  }, []);

  const scrollWithOffset = React.useCallback(
    (element: HTMLElement | null, offset: number) => {
      if (!element || typeof window === 'undefined') return;

      const rect = element.getBoundingClientRect();
      const top = window.scrollY + rect.top - offset;

      window.scrollTo({
        top: Math.max(top, 0),
        behavior: 'smooth',
      });
    },
    [],
  );

  const loadPrograms = React.useCallback(
    async (nextPage = 0, append = false, currentSearch = searchTerm) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const data = await getPrograms({
          page: nextPage,
          search: currentSearch,
          archive: showArchive,
        });

        setPrograms((prev) => (append ? [...prev, ...data] : data));
        setHasMore(data.length === PROGRAMS_PAGE_SIZE);
        setPage(nextPage);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [searchTerm, showArchive],
  );

  React.useEffect(() => {
    if (hasPreloadedData && !searchTerm && !showArchive) {
      setPrograms(normalizePrograms(preloadedData));
      setHasMore(initialPrograms.length === PROGRAMS_PAGE_SIZE);
      setPage(0);
      setLoading(false);
      return;
    }

    const timeout = window.setTimeout(
      () => {
        loadPrograms(0, false, searchTerm);
      },
      searchTerm ? 300 : 0,
    );

    return () => window.clearTimeout(timeout);
  }, [
    hasPreloadedData,
    initialPrograms.length,
    loadPrograms,
    preloadedData,
    searchTerm,
    showArchive,
  ]);

  const handleLoadMore = React.useCallback(() => {
    if (loadingMore || !hasMore || isSearching) return;
    loadPrograms(page + 1, true, '');
  }, [hasMore, isSearching, loadPrograms, loadingMore, page]);

  const handleSearchFocus = React.useCallback(() => {
    if (!isMobile()) return;
    scrollWithOffset(headerRef.current, 12);
  }, [isMobile, scrollWithOffset]);

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setQuery(value);

      if (!isMobile()) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollWithOffset(gridWrapperRef.current, 150);
        });
      });
    },
    [isMobile, scrollWithOffset],
  );

  const handleClearSearch = React.useCallback(() => {
    setQuery('');

    requestAnimationFrame(() => {
      if (isMobile()) {
        scrollWithOffset(headerRef.current, 12);
      }
    });
  }, [isMobile, scrollWithOffset]);

  const handleArchiveChange = React.useCallback(
    (nextValue: boolean) => {
      if (nextValue === showArchive) return;

      setShowArchive(nextValue);
      setPage(0);
      setHasMore(true);
    },
    [showArchive],
  );

  return (
    <div className="programs-view">
      <div className="programs-view__top">
        <main className="programs-view__main">
          <div className="programs-view__header" ref={headerRef}>
            <ProgramsSearch
              value={query}
              showArchive={showArchive}
              onChange={handleSearchChange}
              onArchiveChange={handleArchiveChange}
              onFocus={handleSearchFocus}
              onClear={handleClearSearch}
              resultCount={
                isSearching && !loading ? programs.length : undefined
              }
            />
          </div>

          <div ref={gridWrapperRef}>
            <ProgramsGrid
              items={programs}
              loading={loading && !hasPrograms}
              loadingOverlay={loading && hasPrograms}
              searchTerm={searchTerm}
              emptyMessage={
                isSearching
                  ? showArchive
                    ? `Nenhum programa do acervo encontrado para “${searchTerm}”.`
                    : `Nenhum programa no ar encontrado para “${searchTerm}”.`
                  : showArchive
                    ? 'Nenhum programa disponível no acervo no momento.'
                    : 'Nenhum programa no ar disponível no momento.'
              }
            />

            {!isSearching && hasMore && !loading ? (
              <div className="programs-view__more">
                <button
                  type="button"
                  className="programs-view__moreButton"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </button>
              </div>
            ) : null}
          </div>
        </main>

        <aside className="programs-view__aside">
          <div className="programs-view__asideInner">
            <RadioLiveView
              data={{
                variant: 'compact',
              }}
            />

            <RadioScheduleView
              data={{
                title: 'Programação',
                href: '/programacao',
                footerLabel: 'Ver programação completa',
                itemsToShow: 9,
              }}
            />

            <AdManagerSlot
              slotKey="master"
              className="ad-manager-slot--master"
            />
          </div>
        </aside>
      </div>

      <section className="programs-view__bottom">
        <PodcastsView />
      </section>
    </div>
  );
}
